import { Box, Skeleton } from "@mui/material";

export const ChatHistorySkeleton = () => {
  const skeletonStyles = {
    container: {
      height: "100%",
      display: "flex",
      flexDirection: "column",
      background: "rgba(255, 255, 255, 0.07)",
      backdropFilter: "blur(12px)",
      border: "1px solid rgba(255, 255, 255, 0.14)",
      borderRadius: "20px",
    },
    header: {
      p: 2,
      borderBottom: "1px solid rgba(255, 255, 255, 0.14)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    listContainer: {
      flex: 1,
      p: 1,
      overflow: "hidden",
    },
    listItem: {
      mb: 1,
      mx: 1,
      p: "8px 12px",
      borderRadius: "8px",
      background: "rgba(255, 255, 255, 0.05)",
    },
  };

  return (
    <Box sx={skeletonStyles.container}>
      <Box sx={skeletonStyles.header}>
        <Skeleton
          variant="text"
          width={120}
          height={24}
          sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
        />
        <Skeleton
          variant="circular"
          width={36}
          height={36}
          sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
        />
      </Box>

      <Box sx={skeletonStyles.listContainer}>
        {[...Array(6)].map((_, index) => (
          <Box key={index} sx={skeletonStyles.listItem}>
            <Skeleton
              variant="text"
              width={`${Math.random() * 40 + 60}%`}
              height={20}
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.1)",
                mb: 0.5,
              }}
            />
            <Skeleton
              variant="text"
              width={80}
              height={16}
              sx={{ bgcolor: "rgba(255, 255, 255, 0.08)" }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const ChatMessagesSkeleton = () => {
  const messageSkeletonStyles = {
    container: {
      backgroundColor: "rgba(0, 0, 0, 0.4)",
      borderTopLeftRadius: "20px",
      borderTopRightRadius: "20px",
      height: "calc(100vh - 335px)",
      overflowY: "auto",
      overflowX: "hidden",
      padding: "10px",
      pb: 0,
    },
    messageBox: {
      display: "flex",
      marginBottom: "16px",
    },
    messageBoxRight: {
      justifyContent: "flex-end",
      pr: 2,
    },
    messageBoxLeft: {
      justifyContent: "flex-start",
      pl: 2,
    },
    message: {
      padding: "12px 16px",
      borderRadius: "10px",
      maxWidth: "60%",
      minWidth: "200px",
    },
    messageRight: {
      background: "rgba(138, 88, 203, 0.3)",
      alignSelf: "flex-end",
    },
    messageLeft: {
      background: "rgba(255, 255, 255, 0.1)",
      alignSelf: "flex-start",
    },
    skeletonLine: {
      bgcolor: "rgba(255, 255, 255, 0.2)",
      mb: 0.5,
    },
    lastLine: {
      bgcolor: "rgba(255, 255, 255, 0.15)",
    },
  };

  const generateRandomWidth = () => `${Math.random() * 30 + 70}%`;

  return (
    <Box sx={messageSkeletonStyles.container}>
      <Box
        sx={[
          messageSkeletonStyles.messageBox,
          messageSkeletonStyles.messageBoxRight,
        ]}
      >
        <Box
          sx={[
            messageSkeletonStyles.message,
            messageSkeletonStyles.messageRight,
          ]}
        >
          <Skeleton
            variant="text"
            width={generateRandomWidth()}
            height={20}
            sx={messageSkeletonStyles.skeletonLine}
          />
          <Skeleton
            variant="text"
            width="45%"
            height={20}
            sx={messageSkeletonStyles.lastLine}
          />
        </Box>
      </Box>

      <Box
        sx={[
          messageSkeletonStyles.messageBox,
          messageSkeletonStyles.messageBoxLeft,
        ]}
      >
        <Box
          sx={[
            messageSkeletonStyles.message,
            messageSkeletonStyles.messageLeft,
          ]}
        >
          <Skeleton
            variant="text"
            width="95%"
            height={20}
            sx={messageSkeletonStyles.skeletonLine}
          />
          <Skeleton
            variant="text"
            width="88%"
            height={20}
            sx={messageSkeletonStyles.skeletonLine}
          />
          <Skeleton
            variant="text"
            width="92%"
            height={20}
            sx={messageSkeletonStyles.skeletonLine}
          />
          <Skeleton
            variant="text"
            width="67%"
            height={20}
            sx={messageSkeletonStyles.lastLine}
          />
        </Box>
      </Box>

      <Box
        sx={[
          messageSkeletonStyles.messageBox,
          messageSkeletonStyles.messageBoxRight,
        ]}
      >
        <Box
          sx={[
            messageSkeletonStyles.message,
            messageSkeletonStyles.messageRight,
          ]}
        >
          <Skeleton
            variant="text"
            width={generateRandomWidth()}
            height={20}
            sx={messageSkeletonStyles.skeletonLine}
          />
        </Box>
      </Box>

      <Box
        sx={[
          messageSkeletonStyles.messageBox,
          messageSkeletonStyles.messageBoxLeft,
        ]}
      >
        <Box
          sx={[
            messageSkeletonStyles.message,
            messageSkeletonStyles.messageLeft,
          ]}
        >
          <Skeleton
            variant="text"
            width="85%"
            height={20}
            sx={messageSkeletonStyles.skeletonLine}
            animation="wave"
          />
          <Skeleton
            variant="text"
            width="78%"
            height={20}
            sx={messageSkeletonStyles.skeletonLine}
            animation="wave"
          />
          <Skeleton
            variant="text"
            width="45%"
            height={20}
            sx={messageSkeletonStyles.lastLine}
            animation="wave"
          />
        </Box>
      </Box>
    </Box>
  );
};

export const TypingIndicatorSkeleton = () => {
  const typingStyles = {
    messageBox: {
      display: "flex",
      justifyContent: "flex-start",
      pl: 2,
      marginBottom: "10px",
    },
    message: {
      padding: "12px 16px",
      borderRadius: "10px",
      maxWidth: "60%",
      background: "rgba(255, 255, 255, 0.1)",
      alignSelf: "flex-start",
      display: "flex",
      alignItems: "center",
      minHeight: "20px",
    },
    typingDots: {
      display: "flex",
      gap: "4px",
      alignItems: "center",
    },
    dot: {
      width: "8px",
      height: "8px",
      borderRadius: "50%",
      backgroundColor: "rgba(255, 255, 255, 0.6)",
      animation: "typingDot 1.4s infinite ease-in-out",
      "&:nth-of-type(1)": { animationDelay: "0s" },
      "&:nth-of-type(2)": { animationDelay: "0.2s" },
      "&:nth-of-type(3)": { animationDelay: "0.4s" },
      "@keyframes typingDot": {
        "0%, 80%, 100%": { opacity: 0.3, transform: "scale(0.8)" },
        "40%": { opacity: 1, transform: "scale(1)" },
      },
    },
  };

  return (
    <Box sx={typingStyles.messageBox}>
      <Box sx={typingStyles.message}>
        <Box sx={typingStyles.typingDots}>
          <Box sx={typingStyles.dot} />
          <Box sx={typingStyles.dot} />
          <Box sx={typingStyles.dot} />
        </Box>
      </Box>
    </Box>
  );
};
